import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  CreditCard,
  Sparkles,
  User as UserIcon,
  X,
} from "lucide-react";
import { useNotifications } from "../../context/NotificationsContext";

/* -------------------------------------------------------------------------- */
/*  Visual maps                                                               */
/* -------------------------------------------------------------------------- */

const CATEGORY_VISUALS = {
  quota: {
    bg: "linear-gradient(135deg, #FBBF24 0%, #F59E0B 100%)",
    icon: AlertTriangle,
    iconColor: "#FFFFFF",
  },
  milestone: {
    bg: "linear-gradient(135deg, #38BDF8 0%, #2563EB 100%)",
    icon: Sparkles,
    iconColor: "#FFFFFF",
  },
  account: {
    bg: "linear-gradient(135deg, #A78BFA 0%, #7C3AED 100%)",
    icon: UserIcon,
    iconColor: "#FFFFFF",
  },
  billing: {
    bg: "linear-gradient(135deg, #22D3EE 0%, #0891B2 100%)",
    icon: CreditCard,
    iconColor: "#FFFFFF",
  },
};

/**
 * Severity color system.
 * Tuned to feel like four distinct states at a glance:
 *   danger  = red    (critical, immediate action)
 *   warning = amber  (attention needed, not urgent)
 *   success = green  (positive confirmation)
 *   info    = indigo (neutral update, kept away from the brand-blue CTA color)
 *
 * Each row uses three layers:
 *   - left accent bar (4px solid, full height) when unread
 *   - background tint (Tailwind 50/100 scale, never raw alpha)
 *   - severity pill (always shown when unread, for consistent scanning)
 */
const SEVERITY_VISUALS = {
  danger: {
    accent: "#DC2626",
    ring: "rgba(220,38,38,0.18)",
    unreadBg: "#FEF2F2",     // red-50
    unreadHoverBg: "#FEE2E2", // red-100
    pillBg: "#FEE2E2",
    pillText: "#991B1B",
    pillLabel: "Critical",
  },
  warning: {
    accent: "#D97706",
    ring: "rgba(217,119,6,0.18)",
    unreadBg: "#FFFBEB",     // amber-50
    unreadHoverBg: "#FEF3C7", // amber-100
    pillBg: "#FEF3C7",
    pillText: "#92400E",
    pillLabel: "Warning",
  },
  success: {
    accent: "#059669",
    ring: "rgba(5,150,105,0.18)",
    unreadBg: "#ECFDF5",     // emerald-50
    unreadHoverBg: "#D1FAE5", // emerald-100
    pillBg: "#D1FAE5",
    pillText: "#065F46",
    pillLabel: "Success",
  },
  info: {
    accent: "#4F46E5",
    ring: "rgba(79,70,229,0.18)",
    unreadBg: "#EEF2FF",     // indigo-50
    unreadHoverBg: "#E0E7FF", // indigo-100
    pillBg: "#E0E7FF",
    pillText: "#3730A3",
    pillLabel: "Info",
  },
};

const READ_HOVER_BG = "#FAFAFB";

/* -------------------------------------------------------------------------- */
/*  Sub-components                                                            */
/* -------------------------------------------------------------------------- */

const CategoryIcon = ({ category, dimmed, severityRing }) => {
  const visual = CATEGORY_VISUALS[category] || CATEGORY_VISUALS.account;
  const Icon = visual.icon;
  return (
    <span
      className="shrink-0 inline-flex items-center justify-center w-10 h-10 rounded-[11px]"
      style={{
        background: visual.bg,
        // When unread, a 3px severity-colored halo wraps the icon.
        // When read, the icon flattens into a subtle drop-shadow only.
        boxShadow: dimmed
          ? "0 1px 2px rgba(15,23,42,0.04)"
          : severityRing
            ? `0 0 0 3px ${severityRing}, 0 2px 6px rgba(15,23,42,0.08), inset 0 1px 0 rgba(255,255,255,0.18)`
            : "0 2px 6px rgba(15,23,42,0.10), inset 0 1px 0 rgba(255,255,255,0.18)",
        opacity: dimmed ? 0.72 : 1,
      }}
      aria-hidden="true"
    >
      <Icon size={19} color={visual.iconColor} strokeWidth={2.1} />
    </span>
  );
};

const NotificationItem = ({ notification, onClick }) => {
  const { read, title, body, timeLabel, category, severity } = notification;
  const sev = SEVERITY_VISUALS[severity] || SEVERITY_VISUALS.info;

  const baseBg = read ? "#FFFFFF" : sev.unreadBg;
  const hoverBg = read ? READ_HOVER_BG : sev.unreadHoverBg;

  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = hoverBg;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = baseBg;
      }}
      style={{ background: baseBg }}
      className="relative w-full text-left flex items-start gap-3 pl-4 pr-4 py-3.5 transition-colors duration-150 focus:outline-none focus-visible:bg-[#F3F4F6]"
    >
      {/* Full-height severity accent bar — only on unread */}
      {!read && (
        <span
          aria-hidden="true"
          className="absolute left-0 top-0 bottom-0 w-[4px]"
          style={{ background: sev.accent }}
        />
      )}

      <CategoryIcon
        category={category}
        dimmed={read}
        severityRing={!read ? sev.ring : null}
      />

      <div className="min-w-0 flex-1">
        {/* Title row: title left, timestamp right */}
        <div className="flex items-start justify-between gap-3">
          <h3
            className={`text-[13.5px] leading-snug tracking-[-0.005em] ${read
              ? "font-medium text-[#475569]"
              : "font-semibold text-[#0F172A]"
              }`}
          >
            {title}
          </h3>
          <span
            className={`shrink-0 text-[11px] mt-[2px] font-medium whitespace-nowrap ${read ? "text-[#94A3B8]" : "text-[#64748B]"
              }`}
          >
            {timeLabel}
          </span>
        </div>

        {/* Body */}
        {body && (
          <p
            className={`mt-1 text-[12.5px] leading-[1.45] line-clamp-2 ${read ? "text-[#94A3B8]" : "text-[#4B5563]"
              }`}
          >
            {body}
          </p>
        )}

      </div>
    </button>
  );
};

const EmptyState = ({ tab }) => (
  <div className="px-6 py-14 text-center">
    <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-br from-[#F3F4F6] to-[#E5E7EB] mb-4 shadow-inner">
      <CheckCircle2 size={26} className="text-[#94A3B8]" strokeWidth={1.8} />
    </div>
    <div className="text-[14px] font-semibold text-[#0F172A] tracking-[-0.005em]">
      {tab === "unread" ? "You're all caught up" : "No notifications yet"}
    </div>
    <div className="mt-1.5 text-[12.5px] text-[#64748B] leading-snug max-w-[260px] mx-auto">
      {tab === "unread"
        ? "New notifications will appear here as they arrive."
        : "We'll let you know when something important happens."}
    </div>
  </div>
);

/* -------------------------------------------------------------------------- */
/*  Main dropdown                                                             */
/* -------------------------------------------------------------------------- */

const NotificationDropdown = ({ onClose, anchorRef }) => {
  const panelRef = useRef(null);
  const [tab, setTab] = useState("all"); // 'all' | 'unread'
  const { notifications, unreadCount, markAsRead, markAllRead } =
    useNotifications();

  useEffect(() => {
    const onDown = (e) => {
      if (!panelRef.current) return;
      if (panelRef.current.contains(e.target)) return;
      if (anchorRef?.current && anchorRef.current.contains(e.target)) return;
      onClose();
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
    };
  }, [onClose, anchorRef]);

  const visible = useMemo(
    () =>
      tab === "unread"
        ? notifications.filter((n) => !n.read)
        : notifications,
    [notifications, tab]
  );

  return (
    <>
      {/* Mobile backdrop */}
      <div
        className="fixed inset-0 bg-black/35 backdrop-blur-[2px] z-[120] sm:hidden"
        onClick={onClose}
        aria-hidden="true"
      />

      <div
        ref={panelRef}
        role="dialog"
        aria-label="Notifications"
        className="
          fixed inset-x-0 bottom-0 top-[64px] z-[130] bg-white flex flex-col
          sm:absolute sm:inset-auto sm:right-0 sm:top-[calc(100%+12px)]
          sm:w-[420px] sm:max-w-[calc(100vw-24px)] sm:h-auto
          sm:min-h-[440px] sm:max-h-[540px]
          sm:rounded-2xl sm:border sm:border-[#E5E7EB]
          sm:shadow-[0_20px_60px_-15px_rgba(15,23,42,0.25),0_4px_12px_-2px_rgba(15,23,42,0.08)]
          overflow-hidden
        "
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-[#F1F2F4] bg-gradient-to-b from-[#FBFCFD] to-white">
          <div className="flex items-center gap-2.5">
            <h2 className="text-[15.5px] font-bold text-[#0F172A] tracking-[-0.01em]">
              Notifications
            </h2>
            {unreadCount > 0 && (
              <span
                className="inline-flex items-center justify-center min-w-[20px] h-[20px] px-1.5 rounded-full bg-[#EF4444] text-white text-[10.5px] font-bold leading-none shadow-[0_1px_2px_rgba(239,68,68,0.4)]"
                aria-hidden="true"
              >
                {unreadCount > 99 ? "99+" : unreadCount}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={markAllRead}
              disabled={unreadCount === 0}
              className="text-[12.5px] font-semibold text-[#2563EB] hover:text-[#1D4ED8] transition-colors disabled:text-[#CBD5E1] disabled:cursor-not-allowed"
            >
              Mark all read
            </button>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close notifications"
              className="sm:hidden ml-2 inline-flex items-center justify-center w-8 h-8 rounded-full text-[#64748B] hover:bg-[#F1F5F9] transition-colors"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="px-3.5 pt-2.5 pb-2.5 border-b border-[#F1F2F4]">
          <div className="inline-flex items-center gap-1 p-[3px] rounded-full bg-[#F1F5F9]">
            <button
              type="button"
              onClick={() => setTab("all")}
              className={`px-3.5 py-1.5 rounded-full text-[12.5px] font-semibold transition-all ${tab === "all"
                ? "bg-white text-[#0F172A] shadow-[0_1px_3px_rgba(15,23,42,0.08)]"
                : "text-[#64748B] hover:text-[#0F172A]"
                }`}
            >
              All
            </button>
            <button
              type="button"
              onClick={() => setTab("unread")}
              className={`px-3.5 py-1.5 rounded-full text-[12.5px] font-semibold transition-all flex items-center gap-1.5 ${tab === "unread"
                ? "bg-white text-[#0F172A] shadow-[0_1px_3px_rgba(15,23,42,0.08)]"
                : "text-[#64748B] hover:text-[#0F172A]"
                }`}
            >
              Unread
              {unreadCount > 0 && (
                <span
                  className={`inline-flex items-center justify-center min-w-[18px] h-[16px] px-1 rounded-full text-[10px] font-bold leading-none ${tab === "unread"
                    ? "bg-[#0F172A] text-white"
                    : "bg-[#CBD5E1] text-white"
                    }`}
                >
                  {unreadCount > 99 ? "99+" : unreadCount}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* List */}
        <div
          className="flex-1 overflow-y-auto divide-y divide-[#F1F5F9] [&::-webkit-scrollbar]:hidden"
          style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
        >
          {visible.length === 0 ? (
            <EmptyState tab={tab} />
          ) : (
            visible.map((n) => (
              <NotificationItem
                key={n.id}
                notification={n}
                onClick={() => markAsRead(n.id)}
              />
            ))
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-[#F1F2F4] px-4 py-3 text-center bg-gradient-to-t from-[#FBFCFD] to-white">
          {/* <button
            type="button"
            className="text-[12.5px] font-semibold text-[#2563EB] hover:text-[#1D4ED8] transition-colors"
            onClick={onClose}
          >
            View all notifications
          </button> */}
        </div>
      </div>
    </>
  );
};

export default NotificationDropdown;
